/**
 * privacy-sanitizer.ts
 *
 * Ephemeral Salted Privacy Sanitizer, Secret Redactor, Prompt Injection Scanner,
 * and Key-Separated HMAC Generator (K_analytics, K_canary, K_session).
 */

import { sha256Sync } from '../../core/genesis-provisioner.js';
import { HmacKeyPurpose } from './learning-contracts.js';

export interface PrivacyScanResult {
  readonly sanitizedText: string;
  readonly piiDetected: boolean;
  readonly secretsDetected: boolean;
  readonly poisonPatternDetected: boolean;
  readonly detections: readonly {
    readonly type: 'pii' | 'secret' | 'poison';
    readonly category: string;
    readonly matchedSample: string;
  }[];
}

export class PrivacySanitizer {
  private readonly keys: Record<HmacKeyPurpose, string>;
  private readonly currentKeyVersion: string = 'v1';

  constructor(serverSecrets?: string | Partial<Record<HmacKeyPurpose, string>>) {
    const singleSecret = typeof serverSecrets === 'string' ? serverSecrets : undefined;
    const obj = typeof serverSecrets === 'object' ? serverSecrets : undefined;
    this.keys = {
      analytics: obj?.analytics || singleSecret || 'k-analytics-secret-sovereign-2026',
      canary: obj?.canary || singleSecret || 'k-canary-secret-sovereign-2026',
      session: obj?.session || singleSecret || 'k-session-secret-sovereign-2026',
    };
  }

  /**
   * Generates a purpose-separated, key-versioned HMAC using cross-platform sha256Sync.
   * e.g. "v1:hexDigest"
   */
  generateKeyVersionedHmac(purpose: HmacKeyPurpose, id: string, tenantId: string = 'tenant-default'): string {
    const secret = this.keys[purpose];
    const canonicalInput = `${purpose}:${tenantId}:${id}:${secret}`;
    const digest = sha256Sync(canonicalInput).slice(0, 24);
    return `${this.currentKeyVersion}:${digest}`;
  }

  /**
   * Sanitizes input/output text using an ephemeral per-session salt.
   * Placeholders [PERSON_1], [EMAIL_1] are deterministic within the turn,
   * but non-correlatable across sessions. Mapping table is held purely in volatile memory.
   */
  sanitize(text: string, sessionSalt?: string): PrivacyScanResult {
    if (!text) {
      return {
        sanitizedText: '',
        piiDetected: false,
        secretsDetected: false,
        poisonPatternDetected: false,
        detections: [],
      };
    }

    const detections: { type: 'pii' | 'secret' | 'poison'; category: string; matchedSample: string }[] = [];
    let sanitized = text;

    // Ephemeral mapping table held in volatile memory during this execution
    const ephemeralMap = new Map<string, string>();
    let personCounter = 1;
    let emailCounter = 1;
    let phoneCounter = 1;
    let secretCounter = 1;

    // 1. Secrets & API Keys Scanning
    const secretPatterns: [RegExp, string][] = [
      [/(?:bearer\s+|api[_-]?key[:=\s]+|token[:=\s]+)['"]?([a-zA-Z0-9_\-\.]{20,})['"]?/gi, 'API_TOKEN'],
      [/ghp_[a-zA-Z0-9]{36}/g, 'GITHUB_TOKEN'],
      [/sk-[a-zA-Z0-9]{20,}/g, 'OPENAI_KEY'],
      [/-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----[\s\S]*?-----END (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/g, 'PRIVATE_KEY'],
      [/(?:password|passwd|secret)[:=\s]+['"]?([^\s'"]{6,})['"]?/gi, 'PASSWORD'],
    ];

    for (const [regex, category] of secretPatterns) {
      sanitized = sanitized.replace(regex, (match, captured) => {
        const val = captured || match;
        detections.push({ type: 'secret', category, matchedSample: val.slice(0, 6) + '...' });
        if (!ephemeralMap.has(val)) {
          ephemeralMap.set(val, `[SECRET_${secretCounter++}]`);
        }
        return match.replace(val, ephemeralMap.get(val)!);
      });
    }

    // 2. PII Scanning (Email, Phone)
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    sanitized = sanitized.replace(emailRegex, (email) => {
      detections.push({ type: 'pii', category: 'EMAIL', matchedSample: email.slice(0, 3) + '...' });
      if (!ephemeralMap.has(email)) {
        ephemeralMap.set(email, `[EMAIL_${emailCounter++}]`);
      }
      return ephemeralMap.get(email)!;
    });

    const phoneRegex = /\b(?:\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;
    sanitized = sanitized.replace(phoneRegex, (phone) => {
      detections.push({ type: 'pii', category: 'PHONE', matchedSample: phone.slice(0, 4) + '...' });
      if (!ephemeralMap.has(phone)) {
        ephemeralMap.set(phone, `[PHONE_${phoneCounter++}]`);
      }
      return ephemeralMap.get(phone)!;
    });

    // 3. Prompt Injection & Poisoning Pattern Scanning
    const poisonPatterns = [
      /ignore\s+(?:all\s+)?previous\s+instructions/i,
      /disregard\s+(?:all\s+)?prior\s+guidelines/i,
      /you\s+are\s+now\s+in\s+developer\s+mode/i,
      /override\s+system\s+prompt/i,
      /<system_prompt_override>/i,
      /export\s+all\s+(?:data|prompts|credentials)/i,
    ];

    for (const pattern of poisonPatterns) {
      if (pattern.test(sanitized)) {
        detections.push({
          type: 'poison',
          category: 'PROMPT_INJECTION',
          matchedSample: sanitized.slice(0, 30),
        });
      }
    }

    return {
      sanitizedText: sanitized,
      piiDetected: detections.some((d) => d.type === 'pii'),
      secretsDetected: detections.some((d) => d.type === 'secret'),
      poisonPatternDetected: detections.some((d) => d.type === 'poison'),
      detections,
    };
  }
}
